import { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import RelatedPages from '@/components/base/RelatedPages';

const whyPoints = [
  {
    icon: 'ri-alert-line',
    title: '違規罰則嚴重',
    desc: '違反健康食品標示或廣告法規，最高可處百萬元以上罰鍰，並可能被要求下架回收，嚴重損害品牌商譽。',
  },
  {
    icon: 'ri-store-line',
    title: '通路上架門檻',
    desc: '連鎖藥局、超市、電商平台在審核產品時，均會要求提供完整的合規文件，法規不符即無法上架。',
  },
  {
    icon: 'ri-global-line',
    title: '出口市場法規各異',
    desc: '不同國家對保健食品的分類、原料安全性及標示格式有不同規定，出口前必須逐一確認目標市場法規。',
  },
  {
    icon: 'ri-time-line',
    title: '法規變動頻繁',
    desc: '台灣食藥署定期更新原料正面表列、標示規範及廣告審查標準，未能即時跟進可能導致既有產品不符規定。',
  },
];

const keyRegulations = [
  {
    title: '健康食品法規',
    items: [
      '健康食品認證申請（小綠人標章）的條件與流程',
      '一般食品不得宣稱特定保健功效的限制',
      '原料正面表列：哪些成分可用、哪些禁止',
      '每日攝取量上限與安全性評估要求',
    ],
  },
  {
    title: '標示與廣告規範',
    items: [
      '產品名稱、成分、添加物的標示格式',
      '宣稱文字的合法範圍（不得涉及疾病預防或治療）',
      '廣告媒體發布前的審查程序',
      '社群媒體、KOL 合作的廣告法規界定',
    ],
  },
  {
    title: '生產與品管法規',
    items: [
      'HACCP 危害分析的建立要求',
      '產品批次記錄與追溯系統的規範',
      '保存期限測試與標示方式',
    ],
  },
];

const howWeHelp = [
  {
    step: '01',
    icon: 'ri-search-eye-line',
    title: '法規前置審查',
    desc: '在配方確定前，先由法規人員進行原料合規性審查，確認所有成分符合台灣食藥署正面表列及用量規範，從源頭阻絕法規風險。',
  },
  {
    step: '02',
    icon: 'ri-file-list-3-line',
    title: '標示文件協助',
    desc: '提供標示格式建議與文字審查服務，協助客戶在產品名稱、成分標示、宣稱文字等方面符合規定，避免廣告違規。',
  },
  {
    step: '03',
    icon: 'ri-building-2-line',
    title: '健康食品申請支援',
    desc: '如客戶希望申請健康食品認證（小綠人標章），康永可協助準備安全性及功效性評估所需的科學文件與實驗數據。',
  },
  {
    step: '04',
    icon: 'ri-global-line',
    title: '出口法規諮詢',
    desc: '針對有意出口的客戶，提供目標市場法規差異分析，協助準備各市場所需的進口文件與成分申報資料。',
  },
];

const faqs = [
  {
    q: '一般食品和健康食品的法規差異在哪？',
    a: '一般食品只能宣稱「維持健康」的功能性文字，不得涉及特定保健功效；健康食品（小綠人標章）則通過食藥署審查，可合法宣稱特定保健功效，如「有助於改善血脂」。兩者申請門檻、費用與審查時程差異很大。',
  },
  {
    q: '我的產品文案有哪些用字需要特別注意？',
    a: '台灣法規明確禁止一般食品使用涉及「疾病預防、治療、減輕或診斷」的文字，例如「降血壓」、「治失眠」、「改善糖尿病」等。建議改用「有助維持正常代謝」、「幫助調整體質」等合規表達方式。',
  },
  {
    q: '進口原料是否都可以使用？',
    a: '並非所有進口原料都可合法使用。台灣食藥署公告「可供食品使用原料彙整」正面表列，未列入的原料需提交安全性評估資料才能使用。康永在配方開發前即會確認所有原料的合規性。',
  },
  {
    q: '社群廣告的法規限制是什麼？',
    a: '根據食品安全衛生管理法，網路、社群媒體廣告同樣適用食品廣告規範。與 KOL 或網紅合作時，只要提及具體功效或使用心得，即視為廣告，若有誇大不實之嫌，品牌方與廣告發布者均可能受罰。',
  },
  {
    q: '出口產品需要額外認證嗎？',
    a: '是的，各國要求不同。可協助準備所需文件或轉介代辦負責公司。康永具備協助客戶準備這些文件的經驗，可提供初步諮詢。',
  },
];

export default function RegulationContent() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      {/* Hero Banner */}
      <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/fabdd144f2c387b359cbb474c3b41989.jpeg"
            alt="法規風險背景"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/68 to-[#1a3a1a]/30" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb
              items={[
                { label: '首頁', href: '/' },
                { label: '關於康永', href: '/about' },
                { label: '法規風險' },
              ]}
              className="mb-4 text-gray-300"
            />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
              關於康永
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              法規風險
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              產品上市前的法規檢視與風險控管，是品牌能否長期穩定經營的重要基礎。
            </p>
          </div>
        </div>
      </section>

      {/* Why regulation matters */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
            <div className="w-full lg:w-1/2">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
                為什麼法規必須優先
              </span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] leading-tight mb-5">
                法規不只是「後端問題」，<br />它應該排在產品最前面
              </h2>
              <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">
                許多品牌在產品開發完成、甚至上市後才發現法規問題，輕則修改標示、重則下架回收，所有前期投入付諸流水。把法規審查放在產品開發的第一步，才能讓每一份資源都花在刀口上。
              </p>
              <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-8">
                康永生技在每一次合作開始時，都會先進行法規前置審查，確認產品方向、原料選用與宣稱文字在進入生產前已符合規範，讓您的品牌從一開始就站在合規的基礎上。
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {whyPoints.map((p) => (
                  <div key={p.title} className="flex items-start gap-3 bg-[#f7faf7] rounded-xl p-4 border border-gray-100">
                    <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                      <i className={`${p.icon} text-[#4CAF50] text-base`} />
                    </div>
                    <div>
                      <p className="text-[#1a2e1a] font-bold text-[0.875rem] mb-0.5">{p.title}</p>
                      <p className="text-gray-500 text-[0.8125rem] leading-[1.75]">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=regulatory%20affairs%20specialist%20reviewing%20food%20supplement%20compliance%20documents%20checking%20ingredient%20list%20with%20magnifying%20glass%20in%20office%2C%20professional%20health%20product%20regulatory%20review%20process&width=720&height=500&seq=reg-why-01&orientation=landscape"
                  alt="法規審查"
                  className="w-full h-[340px] lg:h-[420px] object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key regulations */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              法規要點
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              保健食品品牌必知的關鍵法規
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              以下彙整品牌在規劃產品時最常遇到的法規面向
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {keyRegulations.map((reg, idx) => (
              <div key={reg.title} className="bg-white rounded-2xl p-7 border border-gray-100">
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-[#4CAF50] font-bold text-[1.5rem] opacity-20 leading-none">0{idx + 1}</span>
                  <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem]">{reg.title}</h3>
                </div>
                <ul className="space-y-3">
                  {reg.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-fill text-[#4CAF50] text-base" />
                      </div>
                      <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How we help */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              康永的協助
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              康永如何協助您精準避開法規紅線
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              從配方設計到廣告文案，康永在每個關鍵節點提供法規把關服務
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howWeHelp.map((h) => (
              <div key={h.step} className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-center justify-between mb-5">
                  <span className="text-[#4CAF50] font-bold text-[1.5rem] opacity-20 leading-none">{h.step}</span>
                  <div className="w-11 h-11 flex items-center justify-center bg-white rounded-xl border border-green-100">
                    <i className={`${h.icon} text-[#4CAF50] text-xl`} />
                  </div>
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-3">{h.title}</h3>
                <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand guarantee */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
            <div className="flex flex-col lg:flex-row">
              <div className="w-full lg:w-1/2">
                <img
                  src="https://readdy.ai/api/search-image?query=business%20team%20shaking%20hands%20successful%20partnership%20agreement%20health%20supplement%20brand%20collaboration%2C%20confident%20professional%20people%20in%20clean%20bright%20modern%20office%2C%20trust%20partnership%20concept&width=720&height=460&seq=reg-guarantee-01&orientation=landscape"
                  alt="康永風險保障"
                  className="w-full h-[280px] lg:h-full object-cover object-top"
                />
              </div>
              <div className="w-full lg:w-1/2 p-8 lg:p-12">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
                  品牌保障
                </span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.625rem] leading-tight mb-5">
                  康永的法規支援<br />能為您的品牌帶來什麼？
                </h2>
                <ul className="space-y-4">
                  {[
                    { icon: 'ri-shield-check-line', text: '上市前完成合規確認，降低被主管機關裁罰的風險' },
                    { icon: 'ri-store-line', text: '符合通路審核標準，順利取得上架資格' },
                    { icon: 'ri-global-line', text: '出口文件齊備，提升海外市場進入效率' },
                    { icon: 'ri-time-line', text: '法規異動即時通知，讓您的產品持續保持合規' },
                    { icon: 'ri-hand-coin-line', text: '避免因法規問題導致的下架損失與召回成本' },
                    { icon: 'ri-award-line', text: '完整的品質文件提升品牌在消費者眼中的可信度' },
                  ].map((item) => (
                    <li key={item.text} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-[#4CAF50] text-base`} />
                      </div>
                      <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              常見問題
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              法規常見疑問
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className={`bg-[#f7faf7] rounded-xl border transition-all duration-200 overflow-hidden ${
                  openFaq === idx ? 'border-[#4CAF50]/40' : 'border-gray-100'
                }`}
              >
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                >
                  <span className={`text-[1rem] font-semibold leading-snug pr-4 ${openFaq === idx ? 'text-[#4CAF50]' : 'text-[#1a2e1a]'}`}>
                    {faq.q}
                  </span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${openFaq === idx ? 'bg-[#4CAF50] text-white' : 'bg-white text-[#4CAF50]'}`}>
                    <i className={`text-sm ${openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                  </div>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600 text-[0.9375rem] leading-[1.75]">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
            開始合作
          </span>
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            讓康永協助您做好法規準備，<br />安心上市、長期經營
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            歡迎聯絡康永生技，我們將為您進行初步的法規可行性評估，協助您的品牌從一開始就走在正確的路上。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact/form"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              諮詢法規可行性
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/cooperation/process"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              了解合作流程
            </Link>
          </div>
        </div>
      </section>
      <RelatedPages items={[
        { title: '其他服務', desc: '法規風險管控、行銃文案、進口原料代購…了解康永的全方位合作加值服務。', href: '/cooperation/services' },
        { title: '包裝設計', desc: '包材選擇、視覺設計到法規標示，一站式解決包裝設計需求。', href: '/services/packaging' },
        { title: '立即洽詢', desc: '填寫諮詢表單，讓康永協助您進行初步的法規可行性評估。', href: '/contact/form' },
      ]} />
    </>
  );
}
